<template>
  <q-layout view="lHh Lpr lFf">
    <q-header class="nx-header">
      <q-toolbar class="q-px-md" style="background-color: #f0f2f6; overflow: hidden">
        <!-- Show chevron toggle in header only when drawer is closed -->
        <q-btn
          v-if="!leftDrawerOpen"
          flat
          round
          dense
          icon="keyboard_double_arrow_right"
          color="grey-8"
          aria-label="Open Sidebar"
          @click="toggleLeftDrawer"
        />

        <!-- Route tabs -->
        <div class="q-ml-md q-mr-md" style="flex: 1; overflow: hidden" v-show="!hideTabs">
          <q-tabs
            v-model="activeTab"
            align="left"
            dense
            shrink
            stretch
            class="text-grey-8"
            :breakpoint="0"
            inline-label
            no-caps
            @update:model-value="onTabChange"
          >
            <q-tab v-for="t in tabs" :key="t.path" :name="t.path">
              <div class="row items-center no-wrap" style="height: 100%">
                <q-icon v-if="t.icon" :name="t.icon" class="q-mr-xs" />
                <span class="q-ml-xs q-mr-sm">{{ t.title }}</span>
                <q-btn
                  v-if="t.path !== '/'"
                  flat
                  dense
                  round
                  size="sm"
                  icon="close"
                  class="q-ml-xs"
                  @click.stop="closeTab(t.path)"
                />
              </div>
            </q-tab>
          </q-tabs>
        </div>

        <q-btn flat round dense icon="notifications_none" color="grey-8" />
        <q-btn flat round dense icon="help_outline" color="grey-8" />
        <q-btn flat round dense aria-label="User menu">
          <q-avatar size="24px">
            <img
              :src="auth.me?.avatar || 'https://avatars.githubusercontent.com/u/93176510?s=48'"
            />
          </q-avatar>
          <q-menu anchor="bottom right" self="top right">
            <q-list style="min-width: 180px">
              <q-item clickable v-ripple @click="openTab('/chart-testing')">
                <q-item-section avatar>
                  <q-icon name="science" />
                </q-item-section>
                <q-item-section>Chart Testing</q-item-section>
              </q-item>
              <q-separator />
              <q-item clickable v-ripple @click="openTab('/settings')">
                <q-item-section avatar>
                  <q-icon name="settings" />
                </q-item-section>
                <q-item-section>Settings</q-item-section>
              </q-item>
              <q-separator />
              <q-item clickable v-ripple @click="onLogout">
                <q-item-section avatar>
                  <q-icon name="logout" />
                </q-item-section>
                <q-item-section>Logout</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </q-toolbar>
    </q-header>
    <q-drawer v-model="leftDrawerOpen" show-if-above bordered :width="260" class="nx-drawer">
      <!-- User Info Section -->
      <div class="q-pa-md">
        <div class="row items-center justify-between">
          <div class="text-body1 text-weight-medium">
            <img
              src="https://i.ibb.co/4RMg7gyG/Central-Java2.png"
              width="160"
              alt="Logo"
              class="nx-logo"
            />
          </div>
          <q-btn
            flat
            dense
            round
            :icon="leftDrawerOpen ? 'keyboard_double_arrow_left' : 'keyboard_double_arrow_right'"
            size="sm"
            aria-label="Toggle Sidebar"
            @click="toggleLeftDrawer"
          >
            <q-tooltip>Toggle Sidebar (Ctrl+B)</q-tooltip>
          </q-btn>
        </div>
      </div>

      <!-- Navigation Menu (from API) -->
      <q-list padding dense v-if="menu.categories.length">
        <q-expansion-item
          v-for="cat in menu.categories"
          :key="cat.code"
          v-model="expandedSections[cat.code]"
          :label="cat.name"
          :header-class="`text-grey-8 text-weight-bold expansion-header ${isSectionActive(cat.code) ? 'section-active' : ''}`"
          expand-icon="expand_more"
          expanded-icon="expand_less"
          default-opened
          dense
        >
          <q-item
            v-for="it in cat.items"
            :key="it.path"
            clickable
            v-ripple
            :active="activeTab === it.path"
            active-class="q-item--active"
            class="nested-item"
            dense
            @click="openTab(it.path)"
          >
            <q-item-section avatar>
              <q-icon :name="it.icon || 'chevron_right'" />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ it.label }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <!-- Status Info at Bottom of Menu -->
        <q-separator class="q-my-md" />
        <q-item dense clickable v-ripple @click="openTab('/vpn-management')">
          <q-item-section>
            <q-item-label class="text-caption text-grey-6">
              VPN {{ vpnStatusSquares }} {{ vpnStatusCircle }}
            </q-item-label>
          </q-item-section>
        </q-item>
        <q-item dense>
          <q-item-section>
            <q-item-label class="text-caption text-grey-6"> Drive {{ driveStatus }} </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>

      <!-- Fallback to static menu if API not yet loaded -->
      <q-list padding dense v-else>
        <q-item
          clickable
          v-ripple
          :active="activeTab === '/'"
          active-class="q-item--active"
          class="nested-item"
          dense
          @click="openTab('/')"
        >
          <q-item-section avatar>
            <q-icon name="home" />
          </q-item-section>
          <q-item-section>
            <q-item-label>Home</q-item-label>
          </q-item-section>
        </q-item>

        <!-- Status Info at Bottom of Menu -->
        <q-separator class="q-my-md" />
        <q-item dense clickable v-ripple @click="openTab('/vpn-management')">
          <q-item-section>
            <q-item-label class="text-caption text-grey-6">
              VPN {{ vpnStatusSquares }} {{ vpnStatusCircle }}
            </q-item-label>
          </q-item-section>
        </q-item>
        <q-item dense>
          <q-item-section>
            <q-item-label class="text-caption text-grey-6"> Drive {{ driveStatus }} </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </q-drawer>

    <q-page-container>
      <keep-alive :max="15" :include="cachedPages">
        <component :is="activeComponent" :key="activeTab" />
      </keep-alive>
    </q-page-container>
  </q-layout>
</template>

<script setup>
import { useAuth } from 'stores/auth-store'
import { useMenu } from 'stores/menu-store'
import { useRouter, useRoute } from 'vue-router'
import { useQuasar } from 'quasar'
import { ref, computed, onMounted, onUnmounted, watch, defineAsyncComponent } from 'vue'
import { initializeApp } from 'firebase/app'
import { getDatabase, ref as dbRef, onValue, off } from 'firebase/database'

// Firebase config
const firebaseConfig = {
  apiKey: 'AIzaSyBhXVdninZaditDnGBLzhDKwWu3Fh416fI',
  authDomain: 'orion-rftuning-id.firebaseapp.com',
  projectId: 'orion-rftuning-id',
  storageBucket: 'orion-rftuning-id.firebasestorage.app',
  messagingSenderId: '836241223760',
  appId: '1:836241223760:web:00302912ce1e9f6c676155',
  databaseURL: 'https://orion-rftuning-id-default-rtdb.asia-southeast1.firebasedatabase.app/',
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)
const database = getDatabase(app)

const auth = useAuth()
const menu = useMenu()
const router = useRouter()
const route = useRoute()
const $q = useQuasar()

const leftDrawerOpen = ref(false)

// VPN Status from Firebase RTDB
const vpnStatus = ref({
  ext_proxy: { status: 'OFFLINE', type: 'external' },
  vpn0: { status: 'OFFLINE', type: 'internal' },
  vpn1: { status: 'OFFLINE', type: 'internal' },
  vpn2: { status: 'OFFLINE', type: 'internal' },
  vpn3: { status: 'OFFLINE', type: 'internal' },
  vpn4: { status: 'OFFLINE', type: 'internal' },
  vpn5: { status: 'OFFLINE', type: 'internal' },
  vpn6: { status: 'OFFLINE', type: 'internal' },
})

// Drive status (static for now)
const driveStatus = ref('🟢')

// Firebase listener reference
let vpnStatusUnsubscribe = null

// Load tabs from localStorage
const loadTabs = () => {
  const saved = localStorage.getItem('openTabs')
  if (saved) {
    try {
      const parsed = JSON.parse(saved)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed
      }
    } catch (e) {
      console.warn('Failed to parse saved tabs')
    }
  }
  return [{ path: '/', title: 'Home', icon: 'home' }]
}

// Tabs state
const tabs = ref(loadTabs())
const activeTab = ref(localStorage.getItem('activeTabPath') || '/')

// Component mapping using defineAsyncComponent
const componentMap = {
  '/': defineAsyncComponent(() => import('pages/IndexPage.vue')),
  '/executive-dashboard': defineAsyncComponent(() => import('pages/ExecutiveDashboard.vue')),
  '/ssh-pre-post': defineAsyncComponent(() => import('src/pages/LiveMocnDashboard.vue')),
  '/daily-kpi': defineAsyncComponent(() => import('pages/DailyKpiMonitoring.vue')),
  '/daily-plmn': defineAsyncComponent(() => import('pages/DailyPlmnMonitoring.vue')),
  '/orion-monitoring': defineAsyncComponent(() => import('pages/OrionMonitoringPage.vue')),
  '/timing-advance': defineAsyncComponent(() => import('pages/TimingAdvancePage.vue')),
  '/import-data': defineAsyncComponent(() => import('pages/ImportDataPage.vue')),
  '/data-integrity': defineAsyncComponent(() => import('pages/DataIntegrityPage.vue')),
  '/chart-testing': defineAsyncComponent(() => import('pages/ChartTestingPage.vue')),
  '/activity-log': defineAsyncComponent(() => import('pages/ActivityLogPage.vue')),
  '/options': defineAsyncComponent(() => import('pages/OptionsPage.vue')),
  '/settings': defineAsyncComponent(() => import('pages/SettingsPage.vue')),
  '/swe-dashboard': defineAsyncComponent(() => import('pages/IndexPage.vue')),
  '/ssh-dashboard': defineAsyncComponent(() => import('pages/IndexPage.vue')),
  '/executive-summary': defineAsyncComponent(() => import('pages/ExecutiveSummaryPage.vue')),
  '/site-references': defineAsyncComponent(() => import('pages/IndexPage.vue')),
  '/cell-mapping': defineAsyncComponent(() => import('pages/CellMappingPage.vue')),
  '/export-data': defineAsyncComponent(() => import('pages/IndexPage.vue')),
  '/report-generator': defineAsyncComponent(() => import('pages/ReportGeneratorPage.vue')),
  '/vpn-management': defineAsyncComponent(() => import('pages/VpnManagement.vue')),
}
// Active component based on activeTab
const activeComponent = computed(() => {
  return componentMap[activeTab.value] || componentMap['/']
})

// Cached pages for keep-alive
const cachedPages = computed(() => {
  // Return page component names that should be cached
  return tabs.value
    .map((t) => {
      const nameMap = {
        '/': 'IndexPage',
        '/executive-dashboard': 'ExecutiveDashboard',
        '/report-generator': 'ReportGenerator',
        '/ssh-pre-post': 'SshPrePostDashboard',
        '/daily-kpi': 'DailyKpiMonitoring',
        '/daily-plmn': 'DailyPlmnMonitoring',
        '/orion-monitoring': 'OrionMonitoringPage',
        '/timing-advance': 'TimingAdvancePage',
        '/import-data': 'ImportDataPage',
        '/data-integrity': 'DataIntegrityPage',
        '/activity-log': 'ActivityLogPage',
        '/chart-testing': 'ChartTestingPage',
        '/options': 'OptionsPage',
        '/settings': 'SettingsPage',
        '/cell-mapping': 'CellMappingPage',
      }
      return nameMap[t.path]
    })
    .filter(Boolean)
})

// VPN Status Display
const vpnStatusSquares = computed(() => {
  const squares = []
  for (let i = 0; i <= 6; i++) {
    const vpn = vpnStatus.value[`vpn${i}`]
    squares.push(vpn?.status === 'ONLINE' ? '🟩' : '🟥')
  }
  return squares.join('')
})

const vpnStatusCircle = computed(() => {
  const extProxy = vpnStatus.value.ext_proxy
  return extProxy?.status === 'ONLINE' ? '🟢' : '🔴'
})

// Route-to-title map
const routeTitle = {
  '/': 'Home',
  '/executive-dashboard': 'Executive Dashboard',
  '/ssh-pre-post': 'SSH Pre-Post',
  '/daily-kpi': 'Daily KPI',
  '/daily-kpi-monitoring': 'Daily KPI Monitoring',
  '/daily-plmn': 'Daily PLMN',
  '/orion-monitoring': 'Orion Central Dashboard',
  '/swe-dashboard': 'SWE Dashboard',
  '/timing-advance': 'Timing Advance',
  '/ssh-dashboard': 'SSH Dashboard',
  '/pac-executive': 'PAC Executive',
  '/site-references': 'Site References',
  '/cell-mapping': 'Cell Mapping',
  '/export-data': 'Export Data',
  '/import-data': 'Import Data',
  '/data-integrity': 'Data Integrity',
  '/activity-log': 'Activity Log',
  '/chart-testing': 'Chart Testing',
  '/options': 'Options',
  '/settings': 'Settings',
}

function openTab(path) {
  const existing = tabs.value.find((t) => t.path === path)
  if (!existing) {
    // Try to find menu label from store if not statically mapped
    let title = routeTitle[path]
    if (!title) {
      try {
        const cat = menu.categories.find((c) => (c.items || []).some((it) => it.path === path))
        const it = cat ? (cat.items || []).find((x) => x.path === path) : null
        if (it && it.label) title = it.label
      } catch (e) {
        /* ignore */
      }
    }
    tabs.value.push({ path, title: title || 'Untitled', icon: undefined })
  }
  // Set active tab and save to localStorage
  activeTab.value = path
  localStorage.setItem('activeTabPath', path)
  saveTabs()
}

function closeTab(path) {
  if (path === '/') return // Home not closeable
  const idx = tabs.value.findIndex((t) => t.path === path)
  if (idx === -1) return
  const isActive = activeTab.value === path
  tabs.value.splice(idx, 1)
  if (isActive) {
    // activate previous tab if available, else Home
    const next = tabs.value[idx - 1] || tabs.value[idx] || tabs.value.find((t) => t.path === '/')
    if (next) {
      activeTab.value = next.path
      localStorage.setItem('activeTabPath', next.path)
    }
  }
  // Save tabs to localStorage
  saveTabs()
}

function saveTabs() {
  localStorage.setItem('openTabs', JSON.stringify(tabs.value))
}

function onTabChange(path) {
  activeTab.value = path
  localStorage.setItem('activeTabPath', path)
}

// Load expanded sections state from localStorage or use defaults
const loadExpandedState = () => {
  const saved = localStorage.getItem('expandedSections')
  if (saved) {
    try {
      return JSON.parse(saved)
    } catch {
      console.warn('Failed to parse saved expanded sections state')
    }
  }
  return {
    overview: true,
    monitoring: true,
    generator: true,
    database: true,
    settings: true,
  }
}

// Expanded sections state
const expandedSections = ref(loadExpandedState())

// Computed property to check if all sections are expanded
const allExpanded = computed(() => {
  return Object.values(expandedSections.value).every((value) => value)
})

// Helper function to check if a section has active route
const isSectionActive = (sectionCodeOrName) => {
  const currentPath = route.path
  const cat = menu.categories.find(
    (c) => c.code === sectionCodeOrName || c.name === sectionCodeOrName,
  )
  if (!cat) return false
  return (cat.items || []).some((it) => it.path === currentPath)
}

function toggleLeftDrawer() {
  leftDrawerOpen.value = !leftDrawerOpen.value
}

function toggleAllSections() {
  const shouldExpand = !allExpanded.value
  Object.keys(expandedSections.value).forEach((key) => {
    expandedSections.value[key] = shouldExpand
  })
}

async function onLogout() {
  await auth.logout()
  router.push({ name: 'login' })
}

// Keyboard shortcuts
const handleKeydown = (event) => {
  // Ctrl/Cmd + B to toggle drawer
  if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
    event.preventDefault()
    toggleLeftDrawer()
  }
  // Ctrl/Cmd + Shift + E to expand/collapse all
  if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'E') {
    event.preventDefault()
    toggleAllSections()
  }
}

// Session management
function checkSession() {
  if (auth.isAuthenticated) {
    auth.fetchMe().catch(() => {
      // Sudah otomatis logout via axios interceptor jika session invalid
    })
  }
}

// Hide tabs on mobile portrait
const isPortrait = ref(false)
const hideTabs = computed(() => $q.platform.is.mobile === true && isPortrait.value)
let _mql = null
let _mqlHandler = null

// Save expanded sections state to localStorage when it changes
onMounted(() => {
  // Load menu from API
  menu.fetchMenus().catch(() => {})
  watch(
    expandedSections,
    (newState) => {
      localStorage.setItem('expandedSections', JSON.stringify(newState))
    },
    { deep: true },
  )

  // Add keyboard event listeners
  document.addEventListener('keydown', handleKeydown)

  // Cek session ke backend setiap kali layout di-mount
  checkSession()

  // Restore active tab from localStorage or use home
  const savedActiveTab = localStorage.getItem('activeTabPath')
  if (savedActiveTab && tabs.value.find((t) => t.path === savedActiveTab)) {
    activeTab.value = savedActiveTab
  } else {
    activeTab.value = '/'
  }

  // Orientation detection
  if (typeof window !== 'undefined' && 'matchMedia' in window) {
    _mql = window.matchMedia('(orientation: portrait)')
    const setPortrait = () => (isPortrait.value = !!_mql.matches)
    _mqlHandler = setPortrait
    setPortrait()
    if (_mql.addEventListener) _mql.addEventListener('change', setPortrait)
    else if (_mql.addListener) _mql.addListener(setPortrait)
  }

  // Listen to VPN status from Firebase RTDB
  const vpnStatusRef = dbRef(database, 'server/orion/vpn_status')
  vpnStatusUnsubscribe = onValue(vpnStatusRef, (snapshot) => {
    const data = snapshot.val()
    if (data) {
      vpnStatus.value = data
    }
  })
})

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  if (_mql) {
    if (_mql.removeEventListener && _mqlHandler) _mql.removeEventListener('change', _mqlHandler)
    else if (_mql.removeListener && _mqlHandler) _mql.removeListener(_mqlHandler)
  }
  // Cleanup Firebase listener
  if (vpnStatusUnsubscribe) {
    vpnStatusUnsubscribe()
    vpnStatusUnsubscribe = null
  }
})

// Cek session setiap kali route berubah (kecuali dari halaman login)
watch(
  () => route.fullPath,
  (newPath, oldPath) => {
    // Jangan cek session jika baru saja login (untuk menghindari race condition)
    if (oldPath !== '/login') {
      checkSession()
    }
  },
)
</script>
<style scoped>
/* Hide scrollbar by default, show on hover - target drawer directly */
.nx-drawer {
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.nx-drawer::-webkit-scrollbar {
  width: 0;
  height: 0;
}

/* Show scrollbar on hover */
.nx-drawer:hover {
  scrollbar-width: thin; /* Firefox */
  -ms-overflow-style: auto; /* IE and Edge */
}

.nx-drawer:hover::-webkit-scrollbar {
  width: 6px;
}

.nx-drawer:hover::-webkit-scrollbar-track {
  background: transparent;
}

.nx-drawer:hover::-webkit-scrollbar-thumb {
  background: #bbb;
  border-radius: 3px;
}

.nx-drawer:hover::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* Also target any internal scroll containers */
.nx-drawer :deep(*) {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.nx-drawer :deep(*::-webkit-scrollbar) {
  width: 0;
  height: 0;
}

.nx-drawer:hover :deep(*) {
  scrollbar-width: thin;
}

.nx-drawer:hover :deep(*::-webkit-scrollbar) {
  width: 6px;
}

.nx-drawer:hover :deep(*::-webkit-scrollbar-track) {
  background: transparent;
}

.nx-drawer:hover :deep(*::-webkit-scrollbar-thumb) {
  background: #bbb;
  border-radius: 3px;
}
</style>
